import { useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { MarsinetContext } from '../../../../context/marsinetProvider';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { noCompactor } from 'react-grid-layout/core';
import { Button } from 'primereact/button';
import { apiServices } from '../../../../services/apiServices';
import { analytics } from 'apps/marsinet/src/firebase';
import { logEvent } from 'firebase/analytics';
import {
  DashboardContent,
  DatabaseRelationDto,
  DataSourceDto,
  DataSourceDtoTypeEnum,
  MetricDto,
  VisualizationDto,
} from '../../../../typescript-axios';
import ReactGridLayout, { useContainerWidth } from 'react-grid-layout';
import ModalAddDataSource from './components/modal-add-datasource/modal-add-datasource';
import ModalAddVisualization from './components/modal-add-visualization/modal-add-visualization';
import { VISUALIZATION_CONSTRAINTS } from './components/visualizations/config';
import { VisualizationRenderer } from './components/visualizations/visualizations';
import { Dialog } from 'primereact/dialog';
import ModalAddMetric from './components/modal-add-metric/modal-add-metric';
import { AvailableResourceActions, can } from '@marsinet/commons';
import 'react-grid-layout/css/styles.css'; //da error pero compila y son necesarios para los dashboards!
import 'react-resizable/css/styles.css'; //da error pero compila y son necesarios para los dashboards!

export function Dashboard() {
  const { dashboard_id } = useParams<{ dashboard_id: string }>();
  const [dashboard, setDashboard] = useState<DashboardContent | null>(null);
  const { access_token, role, setRole, setAdmin, setError } =
    useContext(MarsinetContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const [savedDashboard, setSavedDashboard] = useState<DashboardContent | null>(
    null
  );
  const [visibleCreateDataSource, setVisibleCreateDataSource] = useState(false);
  const [visibleCreateVisualization, setVisibleCreateVisualization] =
    useState(false);
  const [visibleAddDataSourceChoice, setVisibleAddDataSourceChoice] =
    useState(false);
  const [visibleCreateMetric, setVisibleCreateMetric] = useState(false);
  const [baseDataSources, setBaseDataSources] = useState<DataSourceDto[]>([]);
  const [baseTablesRelations, setBaseTablesRelations] = useState<
    DatabaseRelationDto[]
  >([]);

  /////////////// REACT GRID LAYOUT CONFIG ///////////////

  const gridConfig = { cols: 12, rowHeight: 30 };

  const { width, containerRef, mounted } = useContainerWidth();

  //role !== ROLES.MARSI && role !== ROLES.COMMERCIAL
  const canViewDashboards =
    role && can(role, AvailableResourceActions.dashboards.read_base);

  const layout = useMemo(
    () =>
      (dashboard?.visualizations ?? []).map((viz, i) => ({
        i: String(i),
        x: viz.position.x,
        y: viz.position.y,
        w: viz.size.w,
        h: viz.size.h,
        minW: VISUALIZATION_CONSTRAINTS[viz.type].minW,
        maxW: VISUALIZATION_CONSTRAINTS[viz.type].maxW,
        minH: VISUALIZATION_CONSTRAINTS[viz.type].minH,
        maxH: VISUALIZATION_CONSTRAINTS[viz.type].maxH,
      })),
    [dashboard?.visualizations]
  );

  const handleLayoutChange = (newLayout) => {
    if (!isEditMode) return;

    setDashboard((prev) => {
      if (!prev) return prev;

      const hasChanges = prev.visualizations.some((viz, i) => {
        const item = newLayout.find((l) => l.i === String(i));
        return (
          item &&
          (item.x !== viz.position.x ||
            item.y !== viz.position.y ||
            item.w !== viz.size.w ||
            item.h !== viz.size.h)
        );
      });

      if (!hasChanges) return prev;

      return {
        ...prev,
        visualizations: prev.visualizations.map((viz, i) => {
          const item = newLayout.find((l) => l.i === String(i));
          if (!item) return viz;
          return {
            ...viz,
            position: { x: item.x, y: item.y },
            size: { w: item.w, h: item.h },
          };
        }),
      };
    });
  };

  /////////////////////////////////////////////////

  // Handle mode changes
  const handleEnterEditMode = () => {
    setSavedDashboard(dashboard);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setDashboard(savedDashboard);
    setIsEditMode(false);
  };

  const handleSaveChanges = async () => {
    try {
      await apiServices.dashboardsApi.dashboardsControllerSaveDashboard(
        dashboard
      );
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving dashboard:', error);
    }
  };

  useEffect(() => {
    if (!canViewDashboards) {
      navigate('/authenticated');
    }
  }, [role, navigate]);

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response =
          await apiServices.myAccountApi.myAccountControllerGetData();
        const data = response.data;

        setRole(data.organization?.role);
        setAdmin(data.can_edit);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
            error_page: window.location.pathname,
            error_stack: error.stack || 'No stack trace',
            timestamp: new Date().toISOString(),
            browser_info: navigator.userAgent,
          });
        }
      }
    };

    if (!access_token) return;
    fetchAccountData();
  }, [access_token]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);

      try {
        if (!dashboard_id) return;

        const dashboardResponse =
          await apiServices.dashboardsApi.dashboardsControllerFindOne(
            dashboard_id
          );

        setDashboard(dashboardResponse.data);

        setBaseDataSources(
          (dashboardResponse.data.data_sources ?? []).filter(
            (ds) => ds.type === DataSourceDtoTypeEnum.Base
          )
        );

        const availableBaseTablesRelations =
          await apiServices.dashboardsApi.dashboardsControllerGetAllBaseTablesRelations();
        setBaseTablesRelations(availableBaseTablesRelations.data);
      } catch (error) {
        if (error.response?.status !== 401) {
          setError(true);
          logEvent(analytics, 'error_marsinet', {
            error_message: error.message,
            error_code: error.response?.status,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dashboard_id]);

  const createDataSourceFunction = async (values: {
    dataSource: DataSourceDto;
  }): Promise<void> => {
    // Check no duplicate name
    if (
      dashboard?.data_sources.some((ds) => ds.name === values.dataSource.name)
    ) {
      throw new Error(
        `A data source with the name "${values.dataSource.name}" already exists.`
      );
    }

    // Check the dataSource definition is valid
    const dataSourceValidation =
      await apiServices.dashboardsApi.dashboardsControllerIsDataSourceValid(
        values.dataSource
      );

    if (!dataSourceValidation.data.is_valid) {
      throw new Error(
        `Data source is invalid: ${dataSourceValidation.data.reason}`
      );
    }

    setDashboard((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        data_sources: [...prev.data_sources, values.dataSource],
      };
    });
  };

  const createVisualizationFunction = async (values: {
    visualization: VisualizationDto;
  }): Promise<void> => {
    setDashboard((prev) => {
      if (!prev) return prev;

      const { w, h } = values.visualization.size;
      const position = findFirstAvailablePosition(
        prev.visualizations,
        w,
        h,
        gridConfig.cols
      );

      return {
        ...prev,
        visualizations: [
          ...prev.visualizations,
          {
            ...values.visualization,
            position: { ...values.visualization.position, ...position },
          },
        ],
      };
    });
  };

  const createMetricFunction = async (values: {
    metric: MetricDto;
  }): Promise<void> => {
    if (
      dashboard?.metrics.some((metric) => metric.name === values.metric.name)
    ) {
      throw new Error(
        `A metric with the name "${values.metric.name}" already exists.`
      );
    }

    setDashboard((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        metrics: [...prev.metrics, values.metric],
      };
    });
  };

  const findFirstAvailablePosition = (
    visualizations: VisualizationDto[],
    newW: number,
    newH: number,
    cols: number
  ): { x: number; y: number } => {
    const occupied = new Set(
      visualizations.flatMap((viz) => {
        const cells = [];
        for (
          let row = viz.position.y;
          row < viz.position.y + viz.size.h;
          row++
        ) {
          for (
            let col = viz.position.x;
            col < viz.position.x + viz.size.w;
            col++
          ) {
            cells.push(`${col},${row}`);
          }
        }
        return cells;
      })
    );

    const fitsAt = (x: number, y: number): boolean => {
      for (let row = y; row < y + newH; row++) {
        for (let col = x; col < x + newW; col++) {
          if (col + newW > cols || occupied.has(`${col},${row}`)) return false;
        }
      }
      return true;
    };

    const maxY = Math.max(
      0,
      ...visualizations.map((v) => v.position.y + v.size.h)
    );

    for (let y = 0; y <= maxY; y++) {
      for (let x = 0; x <= cols - newW; x++) {
        if (fitsAt(x, y)) return { x, y };
      }
    }

    return { x: 0, y: maxY }; // fallback: append at bottom
  };

  const groupedSources = {
    base:
      dashboard?.data_sources.filter(
        (ds) => ds.type === DataSourceDtoTypeEnum.Base
      ) ?? [],
    custom:
      dashboard?.data_sources.filter(
        (ds) => ds.type === DataSourceDtoTypeEnum.Custom
      ) ?? [],
    other:
      dashboard?.data_sources.filter(
        (ds) =>
          ds.type !== DataSourceDtoTypeEnum.Base &&
          ds.type !== DataSourceDtoTypeEnum.Custom
      ) ?? [],
  };

  const sections = [
    {
      key: 'base',
      label: t('pages.dashboards.datasource.base'),
      sources: groupedSources.base,
    },
    {
      key: 'custom',
      label: t('pages.dashboards.datasource.custom'),
      sources: groupedSources.custom,
    },
    {
      key: 'other',
      label: t('pages.dashboards.datasource.other'),
      sources: groupedSources.other,
    },
  ];

  const metrics = dashboard?.metrics ?? [];

  return (
    <div className="col-12 xl:pl-7 xl:pr-7">
      <div className="col-12">
        <ConfirmDialog />

        {/* ── Top bar ── */}
        <div className="col-12 shadow-3 surface-card border-round-xl">
          <div className="p-3 flex align-items-center flex-wrap">
            <Button
              icon="pi pi-angle-left"
              rounded
              onClick={() => navigate('/authenticated/dashboards')}
              severity="secondary"
              className="mr-2 mb-2 sm:mr-4"
              aria-label="Go back"
            />
            <div className="mr-2 mb-2">
              <i className="pi pi-chart-line text-2xl md:text-3xl text-900"></i>
            </div>
            <div className="font-medium text-lg sm:text-xl md:text-2xl text-700">
              {dashboard?.name}
            </div>

            {/* ── Action buttons (right side) ── */}
            <div className="ml-auto flex align-items-center gap-2">
              {isEditMode ? (
                <>
                  <Button
                    icon="pi pi-times"
                    label={t('pages.dashboards.cancelEdit')}
                    onClick={handleCancelEdit}
                    severity="secondary"
                    aria-label="Cancel edit"
                    className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
                  />
                  <Button
                    icon="pi pi-check"
                    label={t('pages.dashboards.saveChanges')}
                    onClick={handleSaveChanges}
                    aria-label="Save changes"
                    className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
                    style={{
                      backgroundColor: '#003257',
                      borderColor: '#003257',
                      color: '#fff',
                    }}
                  />
                </>
              ) : (
                dashboard?.is_owner && (
                  <Button
                    icon="pi pi-pencil"
                    label={t('pages.dashboards.edit')}
                    onClick={handleEnterEditMode}
                    severity="secondary"
                    aria-label="Edit dashboard"
                    className="text-sm sm:text-base lg:text-lg px-3 py-2 lg:px-4 lg:py-2"
                    tooltipOptions={{
                      position: 'bottom',
                      mouseTrack: true,
                      mouseTrackTop: 20,
                    }}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body: optional sidebar + grid ── */}
      <div className="col-12 flex mx-0 gap-3 mt-3">
        {/* ── Edit sidebar ── */}
        {isEditMode && (
          <div
            className="surface-card border-round-xl shadow-3 p-3 flex-shrink-0 flex flex-column"
            style={{
              width: '220px',
              height: 'fit-content',
              alignSelf: 'flex-start',
            }}
          >
            {/* Add Visualization */}
            <Button
              icon="pi pi-plus"
              label={t('pages.dashboards.addVisualization')}
              onClick={() => setVisibleCreateVisualization(true)}
              className="w-full mb-3"
            />

            <div className="border-top-1 surface-border mb-3" />

            {/* Data Sources */}
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="font-semibold text-700">
                {t('pages.dashboards.dataSources')}
              </span>
              <Button
                icon="pi pi-plus"
                onClick={() => setVisibleAddDataSourceChoice(true)}
                rounded
                text
                severity="secondary"
                aria-label="Add data source"
                style={{ width: '1.75rem', height: '1.75rem' }}
              />
            </div>

            <div className="flex flex-column gap-3">
              {dashboard?.data_sources.length === 0 && (
                <span className="text-sm text-500">
                  {t('pages.dashboards.noDataSources')}
                </span>
              )}
              {sections.map(
                ({ key, label, sources }) =>
                  sources.length > 0 && (
                    <div key={key} className="flex flex-column gap-1">
                      <span
                        className="text-xs text-400 font-medium uppercase"
                        style={{ letterSpacing: '0.05em' }}
                      >
                        {label}
                      </span>
                      <div
                        style={{ maxHeight: '300px', overflowY: 'auto' }}
                        className="flex flex-column gap-1"
                      >
                        {sources.map((ds) => (
                          <div
                            key={ds.name}
                            className="flex align-items-center gap-2 p-2 border-round hover:surface-hover transition-colors transition-duration-150 cursor-pointer"
                            style={{
                              border: '1px solid var(--surface-border)',
                            }}
                          >
                            <i className="pi pi-database text-primary text-sm" />
                            <span
                              className="text-sm text-700 flex-1"
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                              }}
                            >
                              {ds.name}
                            </span>
                            {ds.type !== DataSourceDtoTypeEnum.Base && (
                              <Button
                                icon="pi pi-trash"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDashboard((prev) => ({
                                    ...prev,
                                    data_sources: prev.data_sources.filter(
                                      (d) => d.name !== ds.name
                                    ),
                                  }));
                                }}
                                rounded
                                text
                                severity="danger"
                                aria-label="Remove data source"
                                style={{
                                  width: '1.5rem',
                                  height: '1.5rem',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
              )}
              {metrics.length > 0 && (
                <div className="flex flex-column gap-1">
                  <span
                    className="text-xs text-400 font-medium uppercase"
                    style={{ letterSpacing: '0.05em' }}
                  >
                    {t('pages.dashboards.datasource.metrics')}
                  </span>
                  <div
                    style={{ maxHeight: '300px', overflowY: 'auto' }}
                    className="flex flex-column gap-1"
                  >
                    {metrics.map((metric) => (
                      <div
                        key={metric.name}
                        className="flex align-items-center gap-2 p-2 border-round hover:surface-hover transition-colors transition-duration-150 cursor-pointer"
                        style={{ border: '1px solid var(--surface-border)' }}
                      >
                        <i className="pi pi-calculator text-primary text-sm" />
                        <span
                          className="text-sm text-700 flex-1"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {metric.name}
                        </span>
                        <Button
                          icon="pi pi-trash"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDashboard((prev) => ({
                              ...prev,
                              metrics: prev.metrics.filter(
                                (m) => m.name !== metric.name
                              ),
                            }));
                          }}
                          rounded
                          text
                          severity="danger"
                          aria-label="Remove data source"
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            flexShrink: 0,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Grid area ── */}
        <div className="py-2 px-3 flex-1 min-w-0">
          <div ref={containerRef}>
            {mounted && (
              <ReactGridLayout
                layout={layout}
                width={width}
                gridConfig={gridConfig}
                compactor={{ ...noCompactor, preventCollision: true }}
                onLayoutChange={handleLayoutChange}
                dragConfig={{ enabled: isEditMode }}
                resizeConfig={{
                  enabled: isEditMode,
                  handles: ['s', 'w', 'e', 'n', 'sw', 'se', 'ne'],
                }}
                onResizeStart={(_, item) => setResizingKey(item.i)}
                onResizeStop={() => setResizingKey(null)}
              >
                {(dashboard?.visualizations ?? []).map((viz, i) => (
                  <div key={String(i)}>
                    {resizingKey !== String(i) && (
                      <VisualizationRenderer
                        visualization={viz}
                        dataSources={dashboard.data_sources}
                        metrics={dashboard.metrics}
                        isEditMode={isEditMode}
                        onDelete={() =>
                          setDashboard(
                            (prev) =>
                              prev && {
                                ...prev,
                                visualizations: prev.visualizations.filter(
                                  (_, idx) => idx !== i
                                ),
                              }
                          )
                        }
                      />
                    )}
                  </div>
                ))}
              </ReactGridLayout>
            )}
          </div>
          {/* ── Add Data Source: choice dialog ── */}
          <Dialog
            header={t('pages.dashboards.addDataSourceOrMetric')}
            visible={visibleAddDataSourceChoice}
            onHide={() => setVisibleAddDataSourceChoice(false)}
            style={{ width: '360px' }}
            blockScroll={true}
            modal
          >
            <div className="flex flex-column gap-3 pt-2">
              <Button
                icon="pi pi-database"
                label={t('pages.dashboards.createDataSource')}
                className="w-full"
                onClick={() => {
                  setVisibleAddDataSourceChoice(false);
                  setVisibleCreateDataSource(true);
                }}
              />
              <Button
                icon="pi pi-calculator"
                label={t('pages.dashboards.createMetric')}
                className="w-full"
                severity="secondary"
                onClick={() => {
                  setVisibleAddDataSourceChoice(false);
                  setVisibleCreateMetric(true);
                }}
              />
            </div>
          </Dialog>
          {/* ── Create Metric modal ── */}
          <ModalAddMetric
            visible={visibleCreateMetric}
            setVisible={setVisibleCreateMetric}
            availableDataSources={dashboard?.data_sources ?? []}
            createMetricFunction={createMetricFunction}
          />
          <ModalAddDataSource
            visible={visibleCreateDataSource}
            setVisible={setVisibleCreateDataSource}
            createDataSourceFunction={createDataSourceFunction}
            baseDataSources={baseDataSources}
            baseTablesRelations={baseTablesRelations}
          />
          <ModalAddVisualization
            visible={visibleCreateVisualization}
            setVisible={setVisibleCreateVisualization}
            availableDataSources={dashboard?.data_sources ?? []}
            availableMetrics={dashboard?.metrics ?? []}
            createVisualizationFunction={createVisualizationFunction}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
